import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { DatePicker, List, message, Spin, Tabs, Tooltip } from 'antd';
import styled from 'styled-components';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import moment from 'moment';
import FormModalStyled from '../../../core/components/modals/FormModalStyled';
import withModal from '../../../core/components/withModals';
import AForm from '../../../core/components/form/AForm';
import ASelect from '../../../core/components/select/ASelect';
import {
  saveUserOjtiList,
  getByStudentId,
  getFreeSubstituteMentors,
  getAllOjtiByAtcu
} from '../../../services/studentMentor.service';
import { useAuthStateValue } from '../../../context/AuthContext';
import AButton from '../../../core/components/buttons/AButton';

const OjtiModal = (props) => {
  const { modalProps, modalData, closeDialog } = props;

  const [selectedTab, setSelectedTab] = useState('defaultOjti');

  const [OJTIList, setOJTIList] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedAtcu, setSelectedAtcu] = useState(null);
  const [setValueFn, setSetValueFn] = useState(null);
  const [defaultOjtiData, setDefaultOjtiData] = useState([]);

  // SUBSTITUTE OJTI TAB STATE
  const [substituteOJTIList, setSubstituteOJTIList] = useState(null);
  const [editingSubstituteItem, setEditingSubstituteItem] = useState(null);
  const [substituteSetValueFn, setSubstituteSetValueFn] = useState(null);
  const [substituteDateFrom, setSubstituteDateFrom] = useState(null);
  const [substituteDateTo, setSubstituteDateTo] = useState(null);
  const [substituteOjtiData, setSubstituteOjtiData] = useState([]);
  const [loading, setLoading] = useState(false);

  const { loggedUser } = useAuthStateValue();
  const { t } = useTranslation();

  const mapOJTISelectOptions = () => {
    const options = [{
      id: null,
      key: null,
      value: null,
      title: ' '
    }];
    if (OJTIList?.[selectedAtcu]?.length > 0) {
      OJTIList?.[selectedAtcu]?.forEach((el) => {
        options.push({
          id: el?.id,
          key: el?.id,
          value: el?.id,
          title: el?.firstName + ' ' + el?.lastName
        });
      });
    }
    return options;
  }

  const mapSubstituteOJTISelectOptions = () => {
    const options = [{
      id: null,
      key: null,
      value: null,
      title: ' '
    }];
    substituteOJTIList?.forEach((el) => {
      options.push({
        id: el?.id,
        key: el?.id,
        value: el?.id,
        title: el?.firstName + ' ' + el?.lastName
      });
    });
    if (editingSubstituteItem) {
      options.push({
        id: editingSubstituteItem?.mentorId,
        key: editingSubstituteItem?.mentorId,
        value: editingSubstituteItem?.mentorId,
        title: editingSubstituteItem?.fullName
      });
    }
    return options;
  };

  const mapAtcuSelectOptions = () => {
    const options = [{
      id: null,
      key: null,
      value: null,
      title: ' '
    }];
    loggedUser?.locationsData?.forEach((el) => {
      options.push({
        id: el?.id,
        key: el?.identificator,
        value: el?.identificator,
        title: el?.name
      })
    });

    return options;
  };

  const fetchStudentOJTIList = async () => {
    try {
      setLoading(true);
      const res = await getByStudentId(modalData?.id);
      const mappedDefaultData = [];
      const mappedSubData = [];
      res?.data?.forEach((el) => {
        if (el?.defaultMentor === true) {
          mappedDefaultData.push({
            id: el?.id,
            studentId: el?.student?.id,
            mentorId: el?.mentor?.id,
            defaultMentor: el?.defaultMentor,
            dateFrom: el?.dateFrom,
            dateTo: el?.dateTo
          });
        } else if (el?.defaultMentor === false) {
          mappedSubData.push({
            id: el?.id,
            studentId: el?.student?.id,
            mentorId: el?.mentor?.id,
            defaultMentor: el?.defaultMentor,
            dateFrom: el?.dateFrom,
            dateTo: el?.dateTo
          });
        }
      });
      setDefaultOjtiData(mappedDefaultData);
      setSubstituteOjtiData(mappedSubData);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchFreeSubstituteOjtiList = async () => {
    try {
      const dateFrom = new Date(substituteDateFrom)?.toISOString()?.substring(0, 10);
      const dateTo = new Date(substituteDateTo)?.toISOString()?.substring(0, 10);
      const res = await getFreeSubstituteMentors(modalData?.id, dateFrom, dateTo);
      setSubstituteOJTIList(res?.data);
    } catch (e) {
      console.log(e);
    }
  };

  const deleteModalHandler = (el, type) => {
    const stateSettingFn = type === 'DEFAULT' ? setDefaultOjtiData : (type === 'SUBSTITUTE' ? setSubstituteOjtiData : null);
    stateSettingFn((prevState) => {
      const changesToKeep = [];
      prevState?.forEach((element) => {
        if (JSON.stringify(element) !== JSON.stringify(el)) changesToKeep.push(element);
      })
      return changesToKeep;
    });
  };

  const renderOjtiDataRows = (type) => {
    const array = [];
    const dataSourceForRendering = type === 'DEFAULT' ? defaultOjtiData : (type === 'SUBSTITUTE' ? substituteOjtiData : []);
    const stateSettingFn = type === 'DEFAULT' ? setEditingItem : (type === 'SUBSTITUTE' ? setEditingSubstituteItem : null);
    dataSourceForRendering?.forEach((el) => {
      let ojtiMatch;
      // eslint-disable-next-line no-restricted-syntax
      for (const [key] of Object.entries(OJTIList)) {
        const matchInCurrentLocation = OJTIList?.[key]?.find((element) => element?.id === el?.mentorId);
        if (matchInCurrentLocation) ojtiMatch = matchInCurrentLocation;
      }
      if ((type === 'DEFAULT' && el?.defaultMentor === true) || (type === 'SUBSTITUTE' && el?.defaultMentor === false)) {
        array.push(
          <List.Item
            actions={[
              <Tooltip title={t('buttons.edit')}>
                <ActionIcon onClick={() => {
                  stateSettingFn({
                    studentId: el?.studentId,
                    mentorId: el?.mentorId,
                    dateFrom: moment(new Date(el?.dateFrom)),
                    dateTo: moment(new Date(el?.dateTo)),
                    atcu: ojtiMatch?.extendUser?.locationIdentificator,
                    fullName: ojtiMatch?.firstName + ' ' + ojtiMatch?.lastName
                  });
                  }}
                >
                  <EditOutlined />
                </ActionIcon>
              </Tooltip>,
              <Tooltip title={t('buttons.delete')}>
                <ActionIcon onClick={() => deleteModalHandler(el, type)}>
                  <DeleteOutlined />
                </ActionIcon>
              </Tooltip>
                ]}
          >
            <List.Item.Meta
              title={ojtiMatch?.firstName + ' ' + ojtiMatch?.lastName}
              description={(
                <div>
                  <span>Od: </span>
                  <b>{new Date(el?.dateFrom).toLocaleDateString('en-GB')}</b>
                  <span> do: </span>
                  <b>{new Date(el?.dateTo).toLocaleDateString('en-GB')}</b>
                </div>
              )}
            />
          </List.Item>
        );
      }
    });

    return array;
  }

  const defaultOjtiFields = [
    {
      id: 'atcu',
      name: t('users.atcu'),
      render: <ASelect options={mapAtcuSelectOptions()} />
    },
    {
      id: 'mentorId',
      name: t('users.OJTI'),
      render: <ASelect options={mapOJTISelectOptions(selectedAtcu)} />,
      disabled: !selectedAtcu
    },
    {
      id: 'dateFrom',
      name: t('users.dateFrom'),
      render: <DatePicker />
    },
    {
      id: 'dateTo',
      name: t('users.dateTo'),
      render: <DatePicker />
    }
  ];

  const substituteOjtiFields = [
    {
      id: 'dateFrom',
      name: t('users.dateFrom'),
      render: <DatePicker />
    },
    {
      id: 'dateTo',
      name: t('users.dateTo'),
      render: <DatePicker />
    },
    {
      id: 'mentorId',
      name: t('users.OJTI'),
      render: <ASelect options={mapSubstituteOJTISelectOptions()} />,
      disabled: !substituteDateFrom || !substituteDateTo
    },
  ];

  const minDate = new Date();

  const validationSchema = yup.object().shape({
    mentorId: yup
        .number()
        .typeError(t('errors.required'))
        .required(t('errors.required')),
    dateFrom: yup
        .date()
        .typeError(t('errors.invalidDate'))
        .required(t('errors.required'))
        .min(minDate, t('errors.dateInPast')),
    dateTo: yup
        .date()
        .typeError(t('errors.invalidDate'))
        .required(t('errors.required'))
  });

  const fetchAllOjtiByAtcu = async (atcu) => {
    try {
      const res = await getAllOjtiByAtcu(atcu);
      setOJTIList((prevState) => {
        const stateToPreserve = { ...prevState };
        stateToPreserve[atcu] = res?.data;
        return stateToPreserve;
      });
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (modalData) {
      fetchStudentOJTIList();
      loggedUser?.locationsData?.forEach((el) => {
        fetchAllOjtiByAtcu(el?.identificator);
      });
    }
  }, [modalData]);

  useEffect(() => {
    if (substituteDateTo && substituteDateTo && modalData) {
      fetchFreeSubstituteOjtiList();
    }
  }, [substituteDateFrom, substituteDateTo, modalData]);

  const updateFormValue = (fieldName, value) => {
    if (setValueFn) {
      setValueFn(fieldName, value);
    }
  };

  const defaultOjtiFormChangeHandler = (formValues) => {
    if (formValues?.atcu !== selectedAtcu) {
      setSelectedAtcu(formValues?.atcu);
      if (
        formValues?.atcu === editingItem?.atcu
        && formValues?.mentorId === editingItem?.mentorId
        && formValues?.studentId === editingItem?.studentId
        && formValues?.dateFrom === editingItem?.dateFrom
        && formValues?.dateTo === editingItem?.dateTo
      ) {
        //
      } else {
        updateFormValue('mentorId', null);
      }
    }
  };

  const substituteOjtiFormChangeHandler = (formValues) => {
    if (!substituteDateFrom && !!formValues?.dateFrom) setSubstituteDateFrom(formValues?.dateFrom);
    if (!substituteDateTo && !!formValues?.dateTo) setSubstituteDateTo(formValues?.dateTo);

    if ((!!substituteDateFrom && !!formValues?.dateFrom) && !formValues?.dateFrom?.isSame(substituteDateFrom)) {
      setSubstituteDateFrom(formValues?.dateFrom);
    }
    if ((!!substituteDateTo && !!formValues?.dateTo) && !formValues?.dateTo?.isSame(substituteDateTo)) {
      setSubstituteDateTo(formValues?.dateTo);
    }
  };

  const addModifiedItem = (formData, type) => {
    const currentData = type === 'DEFAULT' ? [...defaultOjtiData] : (type === 'SUBSTITUTE' ? [...substituteOjtiData] : null);
    const hasOverlapping = currentData?.find((el) => {
      const newItemDateFrom = new Date(formData?.dateFrom);
      const newItemDateTo = new Date(formData?.dateTo);
      const existingItemDateFrom = new Date(el?.dateFrom);
      const existingItemDateTo = new Date(el?.dateTo);
      return newItemDateFrom <= existingItemDateTo && existingItemDateFrom <= newItemDateTo;
    });

    if (hasOverlapping) {
      message.error('Nije moguće dodati OJTI u navedenom periodu. Postoji preklapanje sa postojećim OTJI zaduženjem za studenta.');
    } else if (type === 'DEFAULT') {
        setDefaultOjtiData((prevState) => {
          return [
            ...(prevState ? prevState : null),
            {
              studentId: modalData?.id,
              mentorId: formData?.mentorId,
              defaultMentor: true,
              dateFrom: formData?.dateFrom?.toISOString()?.substring(0, 10),
              dateTo: formData?.dateTo?.toISOString()?.substring(0, 10)
            }
          ];
        });
      } else if (type === 'SUBSTITUTE') {
        setSubstituteOjtiData((prevState) => {
          return [
            ...(prevState ? prevState : null),
            {
              studentId: modalData?.id,
              mentorId: formData?.mentorId,
              defaultMentor: false,
              dateFrom: formData?.dateFrom?.toISOString()?.substring(0, 10),
              dateTo: formData?.dateTo?.toISOString()?.substring(0, 10)
            }
          ];
        });
      }
  };

  const editModifiedItem = (formData, type) => {
    const currentData = type === 'DEFAULT' ? [...defaultOjtiData] : (type === 'SUBSTITUTE' ? [...substituteOjtiData] : null);
    const currentEditingItem = type === 'DEFAULT' ? editingItem : editingSubstituteItem;
    const hasOverlapping = currentData?.find((el) => {
      const newItemDateFrom = new Date(formData?.dateFrom);
      const newItemDateTo = new Date(formData?.dateTo);
      const existingItemDateFrom = new Date(el?.dateFrom);
      const existingItemDateTo = new Date(el?.dateTo);
      const isEditingItem = el?.mentorId === currentEditingItem?.mentorId
        && el?.studentId === currentEditingItem.studentId
        && moment(el?.dateFrom).isSame(currentEditingItem?.dateFrom, 'day')
        && moment(el?.dateTo).isSame(currentEditingItem?.dateTo, 'day');
      return !isEditingItem && newItemDateFrom <= existingItemDateTo && existingItemDateFrom <= newItemDateTo;
    });

    if (hasOverlapping) {
      message.error('Nije moguće dodati OJTI u navedenom periodu. Postoji preklapanje sa postojećim OTJI zaduženjem za studenta.');
    } else if (type === 'DEFAULT') {
        setDefaultOjtiData((prevState) => {
          const changesToKeep = [];
          prevState?.forEach((el) => {
            if (
              editingItem?.studentId === el?.studentId
              && editingItem?.mentorId === el?.mentorId
              && moment(el?.dateFrom).isSame(editingItem?.dateFrom, 'day')
              && moment(el?.dateTo).isSame(editingItem?.dateTo, 'day')
            ) {
              changesToKeep.push({
                studentId: formData?.studentId,
                mentorId: formData?.mentorId,
                defaultMentor: true,
                dateFrom: formData?.dateFrom?.toISOString()?.substring(0, 10),
                dateTo: formData?.dateTo?.toISOString()?.substring(0, 10)
              });
            } else {
              changesToKeep.push(el);
            }
          });
          return changesToKeep;
        });
      } else if (type === 'SUBSTITUTE') {
        setSubstituteOjtiData((prevState) => {
          const changesToKeep = [];
          prevState?.forEach((el) => {
            if (
              editingSubstituteItem?.studentId === el?.studentId
              && editingSubstituteItem?.mentorId === el?.mentorId
              && moment(el?.dateFrom).isSame(editingSubstituteItem?.dateFrom, 'day')
              && moment(el?.dateTo).isSame(editingSubstituteItem?.dateTo, 'day')
            ) {
              changesToKeep.push({
                studentId: formData?.studentId,
                mentorId: formData?.mentorId,
                defaultMentor: false,
                dateFrom: formData?.dateFrom?.toISOString()?.substring(0, 10),
                dateTo: formData?.dateTo?.toISOString()?.substring(0, 10)
              });
            } else {
              changesToKeep.push(el);
            }
          });
          return changesToKeep;
        });
      }
    setEditingItem(null);
  };

  const saveHandler = async () => {
    try {
      setLoading(true);
      const defaultDataToSave = {
        studentId: modalData?.id,
        defaultMentor: true,
        items: []
      };
      const substituteDataToSave = {
        studentId: modalData?.id,
        defaultMentor: false,
        items: []
      };
      defaultOjtiData?.forEach((el) => {
        defaultDataToSave.items.push({
          mentorId: el?.mentorId,
          dateFrom: new Date(el?.dateFrom)?.toISOString()?.substring(0, 10),
          dateTo: new Date(el?.dateTo)?.toISOString()?.substring(0, 10)
        });
      });
      substituteOjtiData?.forEach((el) => {
        substituteDataToSave.items.push({
          mentorId: el?.mentorId,
          dateFrom: new Date(el?.dateFrom)?.toISOString()?.substring(0, 10),
          dateTo: new Date(el?.dateTo)?.toISOString()?.substring(0, 10)
        });
      })
      if (selectedTab === 'defaultOjti') {
        await saveUserOjtiList(defaultDataToSave);
      }
      if (selectedTab === 'substituteOjti') {
        await saveUserOjtiList(substituteDataToSave);
      }
      message.success(t('users.ojtiListSaveSuccess'));
    } catch (e) {
      console.log(e);
      message.error(e?.response?.data ? e?.response?.data : t('users.ojtiListSaveFail'));
    } finally {
      await fetchStudentOJTIList();
      setLoading(false);
      closeDialog();
    }
  };

  const tabs = [
    {
      key: 'defaultOjti',
      label: t('users.defaultOjti'),
      children: (
        <>
          <FormContainer>
            <AForm
              fields={defaultOjtiFields}
              modalData={editingItem}
              style={{ height: '250px' }}
              okButtonText={editingItem ? t('buttons.edit') : t('buttons.add')}
              cancelButtonText={editingItem ? t('buttons.cancel') : null}
              onCancel={() => {
                setEditingItem(null);
              }}
              disabled={loading}
              validationSchema={validationSchema}
              modalAddHandler={(formData) => addModifiedItem(formData, 'DEFAULT')}
              modalEditHandler={(formData) => editModifiedItem(formData, 'DEFAULT')}
              name="add-user-modal-form"
              onFormStateChange={defaultOjtiFormChangeHandler}
              onSetValue={setSetValueFn}
            />
          </FormContainer>
          <ListContainer>
            <List>
              {loading ? <Spin /> : renderOjtiDataRows('DEFAULT')}
            </List>
          </ListContainer>
        </>
      )
    },
    {
      key: 'substituteOjti',
      label: t('users.substituteOjti'),
      children: (
        <>
          <FormContainer>
            <AForm
              style={{ height: '250px' }}
              fields={substituteOjtiFields}
              modalData={editingSubstituteItem}
              okButtonText={editingSubstituteItem ? t('buttons.edit') : t('buttons.add')}
              cancelButtonText={editingSubstituteItem ? t('buttons.cancel') : null}
              onCancel={() => {
                setEditingSubstituteItem(null);
                substituteSetValueFn('dateFrom', null);
                substituteSetValueFn('dateTo', null);
                setSubstituteDateFrom(null);
                setSubstituteDateTo(null);
                setSubstituteOJTIList(null);
              }}
              validationSchema={validationSchema}
              modalAddHandler={(formData) => addModifiedItem(formData, 'SUBSTITUTE')}
              modalEditHandler={(formData) => editModifiedItem(formData, 'SUBSTITUTE')}
              name="add-user-modal-form"
              onFormStateChange={substituteOjtiFormChangeHandler}
              onSetValue={setSubstituteSetValueFn}
            />
          </FormContainer>
          <ListContainer>
            <List>
              {loading ? <Spin /> : renderOjtiDataRows('SUBSTITUTE')}
            </List>
          </ListContainer>
        </>
      )
    }
  ];

  return (
    <FormModalStyled
      centered
      maskClosable={false}
      afterClose={() => {
        setSelectedTab('defaultOjti');
        setEditingItem(null);
        setEditingSubstituteItem(null);
      }}
      width={550}
      footer={[]}
      {...modalProps}
      title={t('users.ojtiModal') + ' - ' + modalData?.firstName + ' ' + modalData?.lastName}
    >
      <TabsContainer>
        <Tabs defaultActiveKey="defaultOjti" items={tabs} onChange={(key) => setSelectedTab(key)} />
      </TabsContainer>
      <ModalFooter>
        <ModalButton disabled={loading} onClick={saveHandler}>
          {t('buttons.save')}
        </ModalButton>
      </ModalFooter>
    </FormModalStyled>
  )
};

export default withModal()(OjtiModal);

const TabsContainer = styled.div`
  width: 100%;
  height: auto;
  padding: 10px;
  box-sizing: border-box;
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #eaeaea;
  border-radius: 5px;
  height: 250px;
  width: calc(100% - 20px);
  overflow-y: scroll;
  margin: 10px;
  padding: 10px;
  & .list-item.selected {
    background-color: #eaeaea;
  }
  & .list-item {
    padding: 10px 10px;
    border-radius: 5px;
  }
`;

const ActionIcon = styled.div`
  cursor: pointer;
`;

const ModalButton = styled(AButton)`
  border-radius: 5px !important;
  box-sizing: border-box;
  &:hover {
    border: none !important;
  }
  &:disabled {
    background-color: #858585 !important;
    &:hover {
      background-color: #858585 !important;
      border: none !important;
    }
  }
`;

const ModalFooter = styled.div`
  border-top: 1px solid #eaeaea;
  width: 100%;
  display:flex;
  justify-content: right;
  padding: 10px 15px 0px 0px;
  gap: 15px;
`;

const FormContainer = styled.div`
  width: 100%;
  height: 320px;
  position: relative;
  & .aform-footer {
    position: absolute;
    bottom: 10px;
    right: 0;
  }
`;
